import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import lodashGet from 'lodash/get';
import debounce from 'lodash/debounce';
import ReactTooltip from 'react-tooltip';
import { v4 as getUUID } from 'uuid';
import noop from 'lodash/noop';

import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Text from '@adsk/bim360-matrix-react-text';

import isContentOverflowingElement from '@adsk/bim360-matrix-react-helpers/es/isContentOverflowingElement';
import { deprecateComponent } from '@adsk/bim360-matrix-react-helpers/es/deprecate';
import tableArrow from '@adsk/bim360-matrix-react-assets/table-arrow.svg';

import './_Table.scss';

/**
 * Data Cell component
 */
class DataCell extends Component {
  /**
   * constructor
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {};
    this.defaultTooltipPosition = 'bottom';
    this.calculateContentOverflow = debounce(this.calculateContentOverflow.bind(this), 500);
  }

  /**
   * Checks whether or not the cell data is overflowing
   */
  calculateContentOverflow() {
    // preventing this to run when component is already unmounted => no errors thrown
    if (this.cellContentText) {
      if (!this.unmounting) {
        this.setState({ truncated: isContentOverflowingElement(this.cellContentText.container) });
      }
    }
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    window.addEventListener('resize', this.calculateContentOverflow);

    this.calculateContentOverflow();
  }

  /**
   * componentWillReceiveProps
   * @param nextProps
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    // In case the data cell text changes
    const value = lodashGet(this.props.rowData, this.props.path);
    const nextValue = lodashGet(nextProps.rowData, nextProps.path);

    if (!nextProps.alwaysRenderTooltip && value !== nextValue) {
      this.calculateContentOverflow();
    }
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    this.unmounting = true;
    window.removeEventListener('resize', this.calculateContentOverflow);
  }

  /**
   * Since sometimes we don't pass all of the relevant data to the Table and instead use customValue prop, we can reach a situation when
   * the Tooltip can't calculate the anchoring properly. This function sets the correct anchoring calculation ref and forces recalculation of anchoring
   *
   */
  syncTooltipAnchoring(anchoringRef) {
    if (!this.tooltipAnchored) {
      // setTimeout to enable changes to propagate before we run the calculateContentOverflow
      setTimeout(() => {
        this.tooltipAnchored = true;
        this.cell = anchoringRef || this.cell;
        this.calculateContentOverflow();
      });
    }
  }

  isTooltipDisabled(props, value, rowData) {
    const { forceTooltip, disableTooltip, alwaysRenderTooltip } = props;
    if (forceTooltip && typeof forceTooltip === 'function' && forceTooltip(value, rowData)) {
      return false;
    }

    return disableTooltip || (!alwaysRenderTooltip && !this.state.truncated);
  }

  /**
   * render
   * @returns {XML}
   */
  render() {
    const {
      customValue,
      rowData,
      path,
      cellStyle,
      className,
      tooltipPosition = this.defaultTooltipPosition,
      tooltipData,
      getTooltipClass,
      rowIndex
    } = this.props;

    // Get cell value from path
    const value = lodashGet(rowData, path);

    const cellClasses = classNames('Table__data-cell', className);
    const cellTextClass = classNames({
      'Table__data-cell-text': !customValue
    });

    const tooltipId = getUUID();
    const tooltipClass = (getTooltipClass || (() => undefined))(value);
    const tooltipClasses = classNames('Table__tooltip', tooltipClass);
    const disableCustomTooltip = this.isTooltipDisabled(this.props, value, rowData);

    // The content of the cell would be either the value, or either a custom value rendered by a component
    const cellContent = customValue ? (
      customValue(value, rowData, rowIndex, path, anchoringRef =>
        this.syncTooltipAnchoring(anchoringRef)
      )
    ) : (
      <Text
        ref={node => {
          this.cellContentText = node;
        }}
        ellipsis={true}
        hideTooltip={true}
      >
        {value}
      </Text>
    );
    const tooltipValue = tooltipData
      ? typeof tooltipData === 'function'
        ? tooltipData(value, rowData)
        : tooltipData
      : cellContent;

    return (
      <div className={cellClasses} style={cellStyle}>
        <div
          data-tip={true}
          data-for={tooltipId}
          className={cellTextClass}
          ref={instance => {
            this.cell = this.cell || instance;
          }}
        >
          {cellContent}
        </div>
        <ReactTooltip
          class={tooltipClasses}
          place={tooltipPosition}
          effect="solid"
          id={tooltipId}
          disable={disableCustomTooltip}
        >
          {tooltipValue}
        </ReactTooltip>
      </div>
    );
  }
}

DataCell.propTypes = {
  cellStyle: PropTypes.object,
  className: PropTypes.string,
  path: PropTypes.string,
  customValue: PropTypes.func,
  rowData: PropTypes.object,
  alwaysRenderTooltip: PropTypes.bool,
  tooltipData: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  tooltipPosition: PropTypes.oneOf(['left', 'top', 'right', 'bottom']),
  disableTooltip: PropTypes.bool,
  forceTooltip: PropTypes.func,
  getTooltipClass: PropTypes.func,
  rowIndex: PropTypes.number
};

/**
 * Header Cell Component
 */
const HeaderCell = props => {
  const {
    currentSort,
    sortField,
    className,
    onColumnSort,
    svgIcon,
    tooltipId,
    customHeader
  } = props;
  const columnClasses = classNames('Table__header-cell', className, {
    'Table__header-cell--hover-enabled': !!sortField,
    'Table__header-cell--current-sort': currentSort && currentSort.sortField === sortField
  });

  const columnNameSpan = props.name ? (
    <span className="Table__header-cell-name">{props.name}</span>
  ) : null;
  const columnIcon = svgIcon ? (
    <SvgIcon width="18px" height="18px" svgId={svgIcon} className="Table__header-cell-icon" />
  ) : null;
  // We're using same SVG for both arrow ascending and descending icon, and just performing 180 deg transform
  const isAscending =
    currentSort && currentSort.sortField === sortField && !currentSort.isAscending;
  const sortIconClass = `Table__header-cell-sort-icon ${
    isAscending ? '' : 'Table__header-cell-sort-icon--descending'
  }`;
  const sortIcon = sortField ? <SvgIcon className={sortIconClass}>{tableArrow}</SvgIcon> : null;

  const headerDivProps = {
    'data-tip': !!tooltipId,
    'data-for': tooltipId,
    className: columnClasses,
    style: props.cellStyle,
    onClick: sortField ? () => onColumnSort(sortField) : null
  };

  const renderStandardHeader = () => {
    return (
      <div {...headerDivProps}>
        {columnNameSpan}
        {columnIcon}
        {sortIcon}
      </div>
    );
  };

  return customHeader ? <div {...headerDivProps}>{customHeader()}</div> : renderStandardHeader();
};

HeaderCell.propTypes = {
  name: PropTypes.string,
  onColumnSort: PropTypes.func,
  sortField: PropTypes.string,
  cellStyle: PropTypes.object,
  className: PropTypes.string,
  svgIcon: PropTypes.string,
  currentSort: PropTypes.shape({ sortField: PropTypes.string, isAscending: PropTypes.bool }),
  tooltipId: PropTypes.string,
  headerTooltip: PropTypes.string,
  headerTooltipClass: PropTypes.string,
  customHeader: PropTypes.func
};

/**
 * Table
 */
export default class Table extends Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    deprecateComponent('Table', 'MatrixTable');
  }

  /**
   * Returns a data row compnent
   * @param rowData - the row data
   * @param rowIndex - the row index
   * @param columns - the columns metadata
   * @returns {XML}
   * @private
   */
  _renderDataRow(rowData, rowIndex, onRowClick, columns, rowRenderer) {
    const children = columns.map((column, columnIndex) => {
      return <DataCell key={columnIndex} rowData={rowData} rowIndex={rowIndex} {...column.props} />;
    });

    if (rowRenderer) {
      return rowRenderer({
        className: 'Table__data-row',
        onClick: onRowClick,
        rowIndex,
        rowData,
        children
      });
    } else {
      return (
        <div key={rowIndex} className="Table__data-row" onClick={() => onRowClick(rowData)}>
          {children}
        </div>
      );
    }
  }

  /**
   * Returns an array of data rows components
   * @param data - the rows data
   * @param columns - the columns metadata
   * @private
   */
  _renderDataRows(data, onRowClick, columns, rowRenderer) {
    return this.props.data.map((rowData, rowIndex) => {
      return this._renderDataRow(rowData, rowIndex, onRowClick || noop, columns, rowRenderer);
    });
  }

  /**
   * Returns the table header row
   * @param columns - the columns metadata
   * @private
   */
  _renderHeaderRow(columns) {
    return React.Children.map(columns, column => {
      const headerTooltipId = getUUID();
      const result = [
        <HeaderCell
          key="1"
          currentSort={this.props.currentSort}
          onColumnSort={this.props.onColumnSort}
          tooltipId={column.props.headerTooltip ? headerTooltipId : null}
          {...column.props}
        />
      ];

      if (column.props.headerTooltip) {
        result.push(
          <ReactTooltip
            key="2"
            class={column.props.headerTooltipClass}
            place="top"
            effect="solid"
            id={headerTooltipId}
          >
            {column.props.headerTooltip}
          </ReactTooltip>
        );
      }

      return result;
    });
  }

  /**
   * @returns {XML}
   */
  render() {
    const classes = classNames('Table', this.props.className);
    const columns =
      this.props.children instanceof Array ? this.props.children : [this.props.children];
    return (
      <div className={classes} style={this.props.style}>
        <div className="Table__header-row">{this._renderHeaderRow(columns)}</div>
        <div className="Table__data-rows">
          {this._renderDataRows(
            this.props.data,
            this.props.onRowClick,
            columns,
            this.props.rowRenderer
          )}
        </div>
      </div>
    );
  }
}

/**
 * Table column - this component is used for defining of a column. This component is not rendered, and instead
 * its properties are passed to HeaderCell and DataCell components and they are rendered.
 */
Table.Column = props => {};

// PropTypes are a merge result of both header cell and data cell properties
Table.Column.propTypes = {
  ...HeaderCell.propTypes,
  ...DataCell.propTypes
};

/**
 * Custom validator for Table.Column component.
 * The Table component doesn't support other types of components.
 * @param props
 * @param propName
 * @param component
 * @param location
 * @param propFullName
 * @returns {*}
 */
PropTypes.tableColumn = (props, propName, component, location, propFullName) => {
  const locationNames = {
    prop: 'prop',
    context: 'context',
    childContext: 'child context'
  };

  if (!(props[propName] && props[propName].type === Table.Column)) {
    return new Error(
      `Invalid ${
        locationNames[location]
      } \`${propFullName}\`, expected component Table.Column but got ${props[propName].type}`
    );
  }

  return null;
};

Table.propTypes = {
  /**
   * Children of the Table component can be either a single Table.Column component or Array<Table.Column>.
   * These children define the columns of the table with their header.
   *
   * Table.Column has the following shape:
   * * name: String? (The name of the column)
   * * path: String? (The path to the field from each row)
   * * className: String? (A custom className to add to each cell (header and data cell)
   * * sortField: String? (A field that sorts the column, which may be different from the path)
   * * customValue: Function? "(value, row, index) => *" (A transformer function that returns the displayed value for each value in the row)
   * * tooltipPosition: String? ['left', 'right', 'top', 'bottom'] (The position of the tooltip when needed to display)
   * * alwaysRenderTooltip: Boolean? (Whether or not the tooltip should always be rendered, preceded by disableTooltip)
   * * disableTooltip: Boolean? (Whether or not the tooltip should be disabled)
   * * forceTooltip: Function? (an override function that determines whether to show the tooltip regardless of text truncation. If it returns true, will show the tooltip regardless)
   * * tooltipData: String?|Function? "(value) => String" (A value to display in the tooltip instead of the data, or a transformer function to get the tooltip content)
   * * getTooltipClass: Function? "(value) => String" (A transformer function to get the class of the tooltip for the cell)
   * * svgIcon: String? (An id of an svg that exists in an SVGStore)
   * * headerTooltip: String? (A text to display as the tooltip for the column header)
   * * headerTooltipClass: String? (The class to add for the tooltip column header)
   */
  //eslint-disable-next-line react/no-typos
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.tableColumn), PropTypes.tableColumn])
    .isRequired,
  /**
   * The data for the table. An array containing all the rows.
   */
  data: PropTypes.array.isRequired,
  /**
   * Handler that will be called when clicking on a single row.
   * The handler is of the shape of "(row) => *"
   */
  onRowClick: PropTypes.func,
  /**
   * An optional Table row renderer. A functions that receives the following parameters and returns a component:
   * {
        className: "Table__data-row",
        onClick: onRowClick,
        rowIndex,
        rowData,
        children
      }
   */
  rowRenderer: PropTypes.func,
  /**
   * Handler that will be called when sorting a column
   * The handler is of the shape of "(sortedFiled) => *"
   */
  onColumnSort: PropTypes.func,
  /**
   * Defines the current sort of the component.
   */
  currentSort: PropTypes.shape({
    /**
     * The field to sort by, as defined in one of the specified Table.Column
     */
    sortField: PropTypes.string,
    /**
     * Whether or not the sort is ascending or descending.
     */
    isAscending: PropTypes.bool
  }),
  /**
   * Style object for the table.
   */
  style: PropTypes.object,
  /**
   * Additional className for the table.
   */
  className: PropTypes.string
};
